import React from 'react';
import {
  Paper,
  Box,
  Typography,
  LinearProgress,
  Chip,
  Tooltip,
  Grid,
} from '@mui/material';
import {
  Slideshow as SlideshowIcon,
  BugReport as BugReportIcon,
} from '@mui/icons-material';
import { Deck } from '../../types';

interface AnalysisProgressProps {
  deck: Deck;
}

export const AnalysisProgress: React.FC<AnalysisProgressProps> = ({ deck }) => {
  const isProcessing = deck.status === 'processing' || deck.status === 'converting';
  
  if (!isProcessing) {
    return null;
  }

  const analyzedSlides = deck.analyzedSlides || 0;
  const totalSlides = deck.totalSlides || 0;
  const isOverallAnalysisPhase = analyzedSlides >= totalSlides && totalSlides > 0;
  
  // Calculate total issues
  const totalIssues = deck.issueCount
    ? deck.issueCount.critical + deck.issueCount.high + deck.issueCount.low
    : 0;

  const getStatusMessage = () => {
    switch (deck.status) {
      case 'converting':
        return 'Converting PowerPoint file...';
      case 'processing':
        return isOverallAnalysisPhase ? 'Analyzing deck with AI...' : 'Analyzing slides with AI...';
      default:
        return 'Processing...';
    }
  };

  const getIssueBreakdownTooltip = () => {
    if (!deck.issueCount || totalIssues === 0) return '';
    
    return (
      <Box>
        <Typography variant="body2" fontWeight={600} mb={1}>
          Issues by Severity:
        </Typography>
        {deck.issueCount.critical > 0 && (
          <Typography variant="body2">
            Critical: {deck.issueCount.critical}
          </Typography>
        )}
        {deck.issueCount.high > 0 && (
          <Typography variant="body2">
            High: {deck.issueCount.high}
          </Typography>
        )}
        {deck.issueCount.low > 0 && (
          <Typography variant="body2">
            Low: {deck.issueCount.low}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6">
          {getStatusMessage()}
        </Typography>
        <Chip label="In Progress" color="warning" />
      </Box>
      
      <LinearProgress sx={{ mb: 2 }} />
      
      {deck.status === 'processing' && totalSlides > 0 && (
        <Grid container spacing={3} mb={2}>
          <Grid item xs={6}>
            <Box display="flex" alignItems="center" gap={1}>
              <SlideshowIcon color="primary" />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Slides Processed
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {analyzedSlides} / {totalSlides}
                </Typography>
              </Box>
            </Box>
          </Grid>
          
          <Grid item xs={6}>
            <Box display="flex" alignItems="center" gap={1}>
              <BugReportIcon color="error" />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Issues Found
                </Typography>
                <Tooltip 
                  title={getIssueBreakdownTooltip()} 
                  arrow 
                  placement="top"
                  enterDelay={300}
                >
                  <Typography 
                    variant="h6" 
                    fontWeight={600}
                    sx={{ 
                      cursor: totalIssues > 0 ? 'help' : 'default',
                      textDecoration: totalIssues > 0 ? 'underline dotted' : 'none',
                    }}
                  >
                    {totalIssues}
                  </Typography>
                </Tooltip>
              </Box>
            </Box>
          </Grid>
        </Grid>
      )}
      
      <Typography variant="body2" color="text.secondary">
        This may take a few minutes depending on the number of slides. 
        You can navigate away and come back later.
      </Typography>
    </Paper>
  );
};

